import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { GraduationCap } from 'lucide-react';
import type { EventInfoEntity } from '../../types/api';
import { borderRadius, colors, spacing, typography } from '../../styles/theme';
import EntityAvatar from '../EntityAvatar';

const hostBadgeStyles = css<{ $university: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
  min-height: 34px;
  max-width: 100%;
  padding: 3px ${spacing[3]} 3px 4px;
  border: 1px solid ${({ $university }) =>
    $university ? colors.warning[300] : 'rgba(255, 255, 255, 0.7)'};
  border-radius: ${borderRadius.full};
  background: ${({ $university }) =>
    $university ? colors.warning[50] : 'rgba(255, 255, 255, 0.96)'};
  color: ${({ $university }) =>
    $university ? colors.warning[800] : colors.primary[800]};
  font-size: ${typography.fontSizes.sm};
  line-height: 1.25;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    filter: brightness(0.97);
  }
`;

const HostLink = styled(Link)<{ $university: boolean }>`
  ${hostBadgeStyles}
`;

const HostLabel = styled.span<{ $university: boolean }>`
  ${hostBadgeStyles}
`;

const HostText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  strong {
    font-weight: ${typography.fontWeights.semibold};
  }
`;

export interface EventPrimaryHostBadgeProps {
  host?: EventInfoEntity | null;
  onClick?: () => void;
}

export const EventPrimaryHostBadge: React.FC<EventPrimaryHostBadgeProps> = ({
  host,
  onClick,
}) => {
  if (!host) return null;
  const name = host.displayName || host.entityName;
  const isUniversity = host.entityType.trim().toLowerCase() === 'university';
  const content = (
    <>
      <span aria-hidden="true">
        <EntityAvatar
          name={name}
          entityType={host.entityType}
          logoUrl={host.logoUrl}
          size={26}
          lazy={false}
        />
      </span>
      {isUniversity && <GraduationCap size={14} aria-hidden="true" />}
      <HostText>Hosted by <strong>{name}</strong></HostText>
    </>
  );

  if (host.canonicalUrl) {
    return (
      <HostLink
        to={host.canonicalUrl}
        $university={isUniversity}
        aria-label={`Hosted by ${name}`}
        title={`Hosted by ${name}`}
        onClick={onClick}
      >
        {content}
      </HostLink>
    );
  }

  return (
    <HostLabel
      $university={isUniversity}
      aria-label={`Hosted by ${name}`}
      title={`Hosted by ${name}`}
    >
      {content}
    </HostLabel>
  );
};

export default EventPrimaryHostBadge;